<script setup lang="ts">
import { watch, onUnmounted } from 'vue'
import { RouterLink, RouterView, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useNotificationStore } from '@/stores/notification.store'
import NotificationBell from '@/components/NotificationBell.vue'

const auth = useAuthStore()
const notifications = useNotificationStore()
const router = useRouter()

// Open a single live notifications connection while signed in; tear it down on sign-out.
watch(
  () => auth.isAuthenticated,
  (authed) => {
    if (authed) {
      void notifications.connect()
    } else {
      void notifications.disconnect()
      notifications.reset()
    }
  },
  { immediate: true },
)

async function signOut() {
  await auth.logout()
  await router.push({ name: 'login' })
}

onUnmounted(() => {
  void notifications.disconnect()
})
</script>

<template>
  <div class="app-shell">
    <header class="app-header">
      <RouterLink to="/" class="brand">🏈 High Fantasy Football</RouterLink>
      <nav class="app-nav">
        <template v-if="auth.isAuthenticated">
          <RouterLink :to="{ name: 'home' }">Dashboard</RouterLink>
          <RouterLink :to="{ name: 'players' }">Players</RouterLink>
          <RouterLink v-if="auth.user?.isSiteAdmin" :to="{ name: 'admin' }">Admin</RouterLink>
          <NotificationBell />
          <span class="user">{{ auth.user?.displayName ?? auth.user?.email }}</span>
          <button class="link-btn" type="button" @click="signOut">Sign out</button>
        </template>
        <RouterLink v-else :to="{ name: 'login' }">Sign in</RouterLink>
      </nav>
    </header>
    <main class="app-main">
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}
.brand {
  font-weight: 700;
  font-size: 1.1rem;
  text-decoration: none;
  color: var(--text);
}
.app-nav {
  display: flex;
  gap: 1rem;
  align-items: center;
}
.app-nav a {
  text-decoration: none;
  color: var(--text-muted);
  font-weight: 600;
}
.app-nav a.router-link-active {
  color: var(--text);
}
.user {
  color: var(--text-muted);
  font-size: 0.9rem;
}
.link-btn {
  border: none;
  background: none;
  padding: 0;
  color: var(--primary);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.app-main {
  flex: 1;
  padding: 1.5rem 1.25rem;
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
}
</style>
